import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '../../ui/chart';
import { Bar, BarChart, XAxis, YAxis, CartesianGrid } from 'recharts';
import { CampaignReportData } from '../../../types/campaignReport';
import { formatCurrencyGBP } from '../../../lib/reportFormatters';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { Info } from 'lucide-react';

interface RevenueTabProps {
  data: CampaignReportData;
}

const chartConfig: ChartConfig = {
  initialBox: {
    label: 'Initial Box Revenue',
    color: '#183df7',
  },
  subscription: {
    label: 'Subscription Revenue',
    color: '#4a6cf7',
  },
};

export function RevenueTab({ data }: RevenueTabProps) {
  // Create monthly breakdown (synthetic if monthly data not available)
  const hasMonthlyData = data.revenue.initialBox.monthly !== undefined;
  const monthlyBreakdown = [];
  
  if (hasMonthlyData && data.revenue.initialBox.monthly && data.revenue.subscription.monthly) {
    for (let i = 1; i <= 6; i++) {
      monthlyBreakdown.push({
        month: `Month ${i}`,
        initialBox: data.revenue.initialBox.monthly!,
        subscription: data.revenue.subscription.monthly!,
        total: data.revenue.initialBox.monthly! + data.revenue.subscription.monthly!,
      });
    }
  } else {
    const avgInitialBox = data.revenue.initialBox.sixMonthTotal / 6;
    const avgSubscription = data.revenue.subscription.sixMonthTotal / 6;
    
    for (let i = 1; i <= 6; i++) {
      monthlyBreakdown.push({
        month: `Month ${i}`,
        initialBox: avgInitialBox,
        subscription: avgSubscription,
        total: avgInitialBox + avgSubscription,
      });
    }
  }

  return (
    <div className="space-y-8">
      {/* Revenue Performance Table */}
      <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
        <CardHeader>
          <CardTitle className="text-xl font-bold" style={{ color: '#183df7' }}>
            4️⃣ Revenue Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">Metric</TableHead>
                <TableHead className="text-right font-semibold">Monthly</TableHead>
                <TableHead className="text-right font-semibold">6-Month Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">Initial Box Revenue</TableCell>
                <TableCell className="text-right">
                  {hasMonthlyData 
                    ? formatCurrencyGBP(data.revenue.initialBox.monthly)
                    : formatCurrencyGBP(data.revenue.initialBox.sixMonthTotal / 6)
                  }
                </TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatCurrencyGBP(data.revenue.initialBox.sixMonthTotal)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Subscription Revenue ({data.revenue.subscription.description || 'blended'})</TableCell>
                <TableCell className="text-right">
                  {hasMonthlyData 
                    ? formatCurrencyGBP(data.revenue.subscription.monthly)
                    : formatCurrencyGBP(data.revenue.subscription.sixMonthTotal / 6)
                  }
                </TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatCurrencyGBP(data.revenue.subscription.sixMonthTotal)}
                </TableCell>
              </TableRow>
              <TableRow className="bg-muted/50 font-bold">
                <TableCell className="font-bold">Total Campaign Revenue</TableCell>
                <TableCell className="text-right">
                  {formatCurrencyGBP(data.revenue.totalCampaignRevenue / 6)}
                </TableCell>
                <TableCell className="text-right font-bold text-lg" style={{ color: '#183df7' }}>
                  {formatCurrencyGBP(data.revenue.totalCampaignRevenue)}
                </TableCell>
              </TableRow>
              {data.revenue.totalTransactionValueProcessed && (
                <TableRow>
                  <TableCell className="font-medium">Total Transaction Value Processed</TableCell>
                  <TableCell colSpan={2} className="text-right font-semibold" style={{ color: '#183df7' }}>
                    {formatCurrencyGBP(data.revenue.totalTransactionValueProcessed)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {!hasMonthlyData && (
            <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
              <Info className="w-4 h-4" />
              <span>Monthly values estimated from 6-month totals</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revenue Chart */}
      <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
        <CardHeader>
          <CardTitle className="text-xl font-bold" style={{ color: '#183df7' }}>
            Revenue by Month
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="h-[350px]">
            <BarChart data={monthlyBreakdown} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 12, fill: '#183df7', fontWeight: 500 }}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#183df7', fontWeight: 500 }}
                tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
              />
              <ChartTooltip 
                content={<ChartTooltipContent />}
                formatter={(value: any) => formatCurrencyGBP(value)}
              />
              <Bar dataKey="initialBox" stackId="revenue" fill="#183df7" radius={[0, 0, 0, 0]} />
              <Bar dataKey="subscription" stackId="revenue" fill="#4a6cf7" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
}
